import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startOfToday, subDays, endOfHour, getUnixTime } from 'date-fns';
import { usePermissions, useSite } from '/src/hooks';

import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { SitePickerBar } from '/src/components/site-picker/index';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { FEATURES, SPECIAL_DATE_RANGES } from '/src/features/_global/constants';
import { TIMEZONE_OFFSET_SECONDS } from '/src/utils/date';
import { dashboardQuery } from '../queries';

export function HomeDashboardContent({ dateRange, sections }) {
  const { sites, selectedSite, selectSite, loadingSites, reloadSites } = useSite();
  const permissions = usePermissions();
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;

  const convertedDateRange = useMemo(() => {
    const start = getUnixTime(subDays(startOfToday(), dateRange)) - TIMEZONE_OFFSET_SECONDS;
    const end = getUnixTime(endOfHour(new Date()));
    let special;
    if (dateRange === 0) {
      special = SPECIAL_DATE_RANGES.TODAY;
    } else if (dateRange === 7) {
      special = SPECIAL_DATE_RANGES.LAST_7_DAYS;
    } else {
      special = SPECIAL_DATE_RANGES.LAST_30_DAYS;
    }

    return {
      start,
      end,
      special,
      previousStart: start - (end - start) - 1,
      previousEnd: start - 1,
    };
  }, [dateRange]);

  const { data, isLoading } = useQuery({
    ...dashboardQuery({
      siteId: selectedSite?.id,
      startAt: convertedDateRange.start,
      endAt: convertedDateRange.end,
      previousStartAt: convertedDateRange.previousStart,
      previousEndAt: convertedDateRange.previousEnd,
    }),
    enabled: Boolean(selectedSite?.id),
  });

  const dashboardData = data?.dashboard;

  if (!loadingSites && !sites.length && canEditSites) {
    return (
      <AddSiteModal
        customTitle="Add a site to get started"
        onExtraSteps={() => {
          reloadSites();
        }}
      />
    );
  }

  return (
    <>
      <SitePickerBar
        sites={sites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        loading={loadingSites}
        addSiteOption={canEditSites}
        pageDisplayName="Home dashboard"
      />
      <>
        <InstallationBanner />
        <div className="flex flex-col p-10">
          <div className="grid grid-flow-row-dense grid-cols-3 gap-5">
            {sections
              .filter((s) => s.enabled)
              .map(({ Component, name }) => (
                <Component key={name} dateRange={convertedDateRange} data={dashboardData} loading={isLoading} />
              ))}
          </div>
        </div>
      </>
    </>
  );
}
